import React, { useState, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { toast } from '../../hooks/use-toast';
import { Layers, Upload, Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const BulkInsuranceUpload = () => {
  const token = localStorage.getItem('token');
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  const upload = async (f) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith('.zip')) {
      toast({ title: 'Please choose a .zip file', variant: 'destructive' });
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const r = await axios.post(`${API}/api/insurance/bulk-card`, fd, { headers });
      setResult(r.data);
      toast({ title: `${r.data.matched_count} cards uploaded`, description: r.data.skipped_count ? `${r.data.skipped_count} file(s) skipped` : 'All files matched.' });
    } catch (e) {
      toast({ title: 'Bulk upload failed', description: e?.response?.data?.detail || 'Try again', variant: 'destructive' });
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <Card data-testid="bulk-insurance-card">
      <CardContent className="p-5 space-y-3">
        <div className="flex items-center gap-2">
          <Layers className="h-5 w-5 text-indigo-600" />
          <h2 className="text-lg font-bold text-gray-900">Bulk Upload Insurance Cards</h2>
        </div>
        <p className="text-sm text-gray-600">
          Upload a <strong>.zip</strong> containing one card per employee (PDF or image). Each file is matched to a staff member by their <strong>first name</strong> in the filename (e.g. <code className="bg-gray-100 px-1 rounded">Ahmed.pdf</code>) and saved as their Insurance Card — replacing last year's. Staff see it instantly in their portal.
        </p>

        <input
          ref={fileRef}
          type="file"
          accept=".zip"
          className="hidden"
          onChange={(e) => upload(e.target.files?.[0])}
          data-testid="bulk-insurance-input"
        />
        <Button
          onClick={() => fileRef.current?.click()}
          disabled={busy}
          className="bg-indigo-600 hover:bg-indigo-700 gap-2"
          data-testid="bulk-insurance-btn"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
          {busy ? 'Uploading…' : 'Upload ZIP of cards'}
        </Button>

        {result && (
          <div className="rounded-lg border bg-gray-50 p-3 text-sm space-y-2" data-testid="bulk-insurance-result">
            <p className="flex items-center gap-2 text-emerald-700 font-medium">
              <CheckCircle2 className="h-4 w-4" /> {result.matched_count} card{result.matched_count === 1 ? '' : 's'} uploaded &amp; assigned
            </p>
            {result.skipped_count > 0 && (
              <div className="text-amber-700">
                <p className="flex items-center gap-2 font-medium"><AlertTriangle className="h-4 w-4" /> {result.skipped_count} file(s) skipped</p>
                <ul className="mt-1 ml-6 list-disc text-xs text-amber-600 max-h-40 overflow-y-auto">
                  {(result.skipped || []).map((s, i) => (
                    <li key={i}><span className="font-mono">{s.file}</span> — {s.reason}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default BulkInsuranceUpload;
